import type { Ballot, FlowCell, FlowRow, MapArgument, Round, RoundNames } from '../schemas/entities';
import { CX_ROW_ID, cellId } from '../usecases/flow';
import { redactor } from './arguments';
import { ALL_SHEET_ID, rowsForSheet, singleSheet } from './sheets';
import { segmentTitle, sheetTitle, sideOfSlot } from './sides';

/**
 * What the model sees of the flow: every row with notes, in order, as `r1`, `r2`… (row ids never leave the device),
 * each speech's notes and the judge's marks, plus the cross-ex notes. Names and team codes are redacted.
 */
export const flowSnapshot = (round: Round, rows: FlowRow[], cells: Map<string, FlowCell>, names: Pick<RoundNames, 'teamA' | 'teamB'> | null | undefined) => {
  const { redact } = redactor(round, names);
  const refs = new Map<string, string>(); // r1 → rowId
  const out: { ref: string; sheet?: string; notes: Record<string, string> }[] = [];
  // One-sheet rounds (or any with one-sheet rows) as one sheet; per-case rounds sheet by sheet, labeled.
  const sheets = rows.some((r) => r.sheetId === ALL_SHEET_ID) ? [singleSheet(round.format)] : round.format.sheets;
  const multi = sheets.length > 1;
  for (const sheet of sheets) {
    for (const row of rowsForSheet(round.format, sheet, rows)) {
      const notes: Record<string, string> = {};
      for (const segId of sheet.segmentIds) {
        const c = cells.get(cellId(row.id, segId));
        if (!c || (!c.text.trim() && c.marks.length === 0)) continue;
        notes[segId] = `${c.text.trim()}${c.marks.length ? ` [judge marked: ${c.marks.join(', ')}]` : ''}`;
      }
      if (Object.keys(notes).length === 0) continue;
      const ref = `r${out.length + 1}`;
      refs.set(ref, row.id);
      out.push({ ref, ...(multi ? { sheet: sheetTitle(round, sheet.title) } : {}), notes });
    }
  }
  const crossEx: Record<string, string> = {};
  for (const seg of round.format.segments.filter((s) => s.kind === 'questioning')) {
    const c = cells.get(cellId(CX_ROW_ID, seg.id));
    if (c?.text.trim()) crossEx[seg.id] = c.text.trim();
  }
  const speeches = round.format.segments.map((s) => ({ id: s.id, name: segmentTitle(round, s), minutes: s.durationSec / 60 }));
  const json = JSON.stringify({ event: round.format.name, resolution: round.resolution || undefined, speeches, rows: out, crossEx }, null, 1);
  return { text: redact(json), refs };
};

/** The judge's current ballot form, as the assistant should see it this turn. */
export interface BallotFields {
  rfd: string;
  teamComments: { A: string; B: string };
  speakerComments: Record<string, string>;
  points: Record<string, number | null>;
}

/**
 * Everything the ballot assistant needs, redacted: the committed decision, the judge's confirmed argument map and
 * weighing, speaker notes and tags, and the form fields as they are right now (rebuilt every turn).
 */
export const ballotContext = (
  round: Round,
  ballot: Ballot,
  fields: BallotFields,
  names: Pick<RoundNames, 'teamA' | 'teamB'> | null | undefined,
) => {
  const r = redactor(round, names);
  const side = (s: 0 | 1 | null | undefined) => (s === 0 || s === 1 ? round.format.sideLabels[s].toUpperCase() : null);
  const teamSide = (team: 'A' | 'B') => r.teamToken(team);
  const keys = (['A', 'B'] as const).flatMap((t) => Array.from({ length: round.format.teamSize }, (_, i) => `${t}${i}`));
  const map = ballot.argumentMap?.arguments ?? [];
  const context = {
    event: round.format.name,
    resolution: round.resolution || undefined,
    winner: side(ballot.winnerSide),
    judgeRoundNotes: ballot.roundNotes || undefined,
    teams: { [teamSide('A')]: round.teamASide === null ? null : side(round.teamASide), [teamSide('B')]: round.teamASide === null ? null : side((1 - round.teamASide) as 0 | 1) },
    arguments: map.map((a) => ({
      title: a.title,
      side: a.side,
      judgeSays: ballot.assessments[a.row]?.survival ?? null,
      judgeNote: ballot.assessments[a.row]?.note || undefined,
      claim: a.claim,
      // The judge's corrections count: dismissed lines are out, moved lines are where the judge put them.
      engaged: a.engaged.filter((e) => e.judge !== 'dismissed').map(({ judge, ...e }) => (judge === 'moved' ? { ...e, placedByJudge: true } : e)),
      extended: a.extended,
      weighed: a.weighed,
      mapStatus: a.status,
    })),
    weighing: {
      [side(0) ?? 'side0']: ballot.weighing.side0,
      [side(1) ?? 'side1']: ballot.weighing.side1,
      judgeSaysWeighingWinner: ballot.weighing.winner === 'side0' ? side(0) : ballot.weighing.winner === 'side1' ? side(1) : ballot.weighing.winner,
      judgeNote: ballot.weighing.note || undefined,
    },
    speakers: keys.map((k) => ({
      speaker: r.speakerToken(k),
      team: teamSide(k[0] as 'A' | 'B'),
      notes: ballot.speakerNotes?.[k]?.text || undefined,
      tags: ballot.speakerNotes?.[k]?.tags ?? [],
      points: fields.points[k] ?? null,
    })),
    currentBallot: {
      rfd: fields.rfd,
      teamComments: { [teamSide('A')]: fields.teamComments.A, [teamSide('B')]: fields.teamComments.B },
      speakerComments: Object.fromEntries(keys.map((k) => [r.speakerToken(k), fields.speakerComments[k] ?? ''])),
    },
  };
  return r.redact(JSON.stringify(context, null, 1));
};

/** "PRO must carry an argument into S1 (Summary) and FF1 (Final Focus); CON into S2 and FF2." */
export const extensionRules = (round: Pick<Round, 'format' | 'firstSide'>) => {
  const f = round.format;
  const parts = (['first', 'second'] as const).map((slot) => {
    const side = sideOfSlot(round, slot);
    const who = side === null ? (slot === 'first' ? 'The team speaking first' : 'The team speaking second') : f.sideLabels[side].toUpperCase();
    const segs = f.segments.filter((s) => s.by === slot && f.extensionRoles.includes(s.role));
    return `${who} must carry an argument into ${segs.map((s) => `${s.id} (${s.label})`).join(' and ') || 'its last speech'}`;
  });
  return `${parts.join('; ')}.${f.extensionNote ? ` ${f.extensionNote}` : ''}`;
};

/**
 * The map as the model returned it (row refs like "r3") → stored form (real row ids), clipped to the stored limits.
 * Arguments whose row doesn't exist are dropped.
 */
export const resolveMap = (args: MapArgument[], refs: Map<string, string>): MapArgument[] => {
  const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
  const ref = (r: string | null) => (r ? refs.get(r) ?? null : null);
  return args.slice(0, 60).flatMap((a) => {
    const row = refs.get(a.row);
    if (!row) return [];
    return [{
      row,
      side: clip(a.side, 20),
      title: clip(a.title, 120),
      claim: clip(a.claim, 600),
      engaged: a.engaged.slice(0, 20).map((e) => ({ speech: clip(e.speech, 12), row: ref(e.row), response: clip(e.response, 600), answered: e.answered, answeredIn: e.answeredIn ? clip(e.answeredIn, 12) : null })),
      extended: a.extended.slice(0, 12).map((e) => ({ speech: clip(e.speech, 12), present: e.present, row: ref(e.row) })),
      weighed: a.weighed.slice(0, 12).map((w) => ({ speech: clip(w.speech, 12), text: clip(w.text, 600) })),
      status: a.status,
      why: clip(a.why, 600),
    }];
  });
};

/** The map's status as a suggestion for the judge's alive / ? / out call. */
export const mapSuggestion = (status: MapArgument['status']): 'alive' | 'dead' | 'unsure' => (status === 'alive' ? 'alive' : status === 'dead' ? 'dead' : 'unsure');

/** Where a map line came from, relative to its argument: its own row, another row, a cross-ex note, or unknown. */
export const lineSource = (argumentRow: string, line: { row: string | null; speech: string }, format: Pick<Round['format'], 'segments'>) => {
  if (line.row === argumentRow) return 'same-row' as const;
  if (line.row) return 'other-row' as const;
  return format.segments.find((s) => s.id === line.speech)?.kind === 'questioning' ? ('cross-ex' as const) : ('unknown' as const);
};

/** The judge moves a response to the argument it's really about. It keeps its details and is marked as moved. */
export const moveResponse = (map: MapArgument[], fromRow: string, index: number, toRow: string): MapArgument[] => {
  const line = map.find((a) => a.row === fromRow)?.engaged[index];
  if (!line || fromRow === toRow || !map.some((a) => a.row === toRow)) return map;
  return map.map((a) =>
    a.row === fromRow ? { ...a, engaged: a.engaged.filter((_, i) => i !== index) }
      : a.row === toRow ? { ...a, engaged: [...a.engaged, { ...line, judge: 'moved' as const }] }
        : a);
};

/** The judge marks a response as not about this argument (or undoes that). Dismissed lines are left out of the ballot context. */
export const dismissResponse = (map: MapArgument[], row: string, index: number, dismissed: boolean): MapArgument[] =>
  map.map((a) => (a.row !== row ? a : {
    ...a,
    engaged: a.engaged.map((e, i) => {
      if (i !== index) return e;
      const { judge: _judge, ...rest } = e;
      return dismissed ? { ...rest, judge: 'dismissed' as const } : rest;
    }),
  }));

/** Has the judge corrected anything on this map? (Mapping again would discard it.) */
export const mapHasCorrections = (map: MapArgument[]) => map.some((a) => a.engaged.some((e) => e.judge));
